import React, { useState, useEffect } from 'react';

interface ParsedContact {
  storeName: string;
  email: string;
  rawLine: string;
}

interface VCardContact {
  name: string;
  phones: string[];
  emails: string[];
  address: string;
  organization: string;
}

export const ContactParser: React.FC = () => {
  const [inputText, setInputText] = useState('');
  const [parsedContacts, setParsedContacts] = useState<ParsedContact[]>([]);
  const [vcardContacts, setVcardContacts] = useState<VCardContact[]>([]);
  const [vcardFileName, setVcardFileName] = useState<string>('');
  const [showOnlyWithEmail, setShowOnlyWithEmail] = useState(false);
  const [hiddenContacts, setHiddenContacts] = useState<Set<number>>(new Set());
  const [showHidden, setShowHidden] = useState(false);
  const [isCompactView, setIsCompactView] = useState(() => {
    const saved = localStorage.getItem('contactTableViewMode');
    return saved === 'compact';
  });
  const [duplicateRemoverText, setDuplicateRemoverText] = useState('');
  const [duplicateLineIndices, setDuplicateLineIndices] = useState<Set<number>>(new Set());

  // Save view mode preference to localStorage
  useEffect(() => {
    localStorage.setItem('contactTableViewMode', isCompactView ? 'compact' : 'full');
  }, [isCompactView]);

  const handleParse = () => {
    if (!inputText.trim()) {
      setParsedContacts([]);
      return;
    }

    try {
      const contacts: ParsedContact[] = [];
      const lines = inputText.split('\n').filter(line => line.trim());

      for (const line of lines) {
        // Parse space-delimited format (2+ consecutive spaces or tabs)
        const spaceDelimitedPattern = /\s{2,}|\t+/;
        if (spaceDelimitedPattern.test(line)) {
          const fields = line
            .split(spaceDelimitedPattern)
            .map(f => f.trim())
            .filter(f => f);

          if (fields.length >= 2) {
            contacts.push({
              storeName: fields[0],
              email: fields[1],
              rawLine: line
            });
          }
        }
      }

      setParsedContacts(contacts);
    } catch (error) {
      setParsedContacts([]);
    }
  };

  const parseVCard = (vcardText: string): VCardContact[] => {
    const contacts: VCardContact[] = [];
    const vcards = vcardText.split(/BEGIN:VCARD/i).filter(v => v.trim());

    for (const vcard of vcards) {
      const lines = vcard.split(/\r?\n/).filter(line => line.trim());
      const contact: VCardContact = {
        name: '',
        phones: [],
        emails: [],
        address: '',
        organization: ''
      };

      let currentProperty = '';

      for (let i = 0; i < lines.length; i++) {
        let line = lines[i].trim();

        // Handle line folding (continuation lines start with space or tab)
        while (i + 1 < lines.length && /^[ \t]/.test(lines[i + 1])) {
          line += lines[i + 1].trim();
          i++;
        }

        // Parse property and value
        const colonIndex = line.indexOf(':');
        if (colonIndex === -1) continue;

        const propertyPart = line.substring(0, colonIndex);
        const value = line.substring(colonIndex + 1).trim();

        // Extract property name (before ; or :)
        const property = propertyPart.split(';')[0].toUpperCase();

        // Decode quoted-printable if needed (vCard 2.1)
        let decodedValue = value;
        if (propertyPart.includes('ENCODING=QUOTED-PRINTABLE')) {
          decodedValue = decodeQuotedPrintable(value);
        }

        switch (property) {
          case 'FN': // Full Name (vCard 3.0)
            contact.name = decodedValue;
            break;
          case 'N': // Name (vCard 2.1 and 3.0)
            if (!contact.name) {
              // N format: LastName;FirstName;MiddleName;Prefix;Suffix
              const nameParts = decodedValue
                .split(';')
                .map(p => p.trim())
                .filter(p => p);
              contact.name = nameParts.join(' ');
            }
            break;
          case 'TEL': // Telephone
            if (decodedValue) {
              contact.phones.push(decodedValue);
            }
            break;
          case 'EMAIL':
            if (decodedValue) {
              contact.emails.push(decodedValue);
            }
            break;
          case 'ADR': // Address
            // ADR format: POBox;ExtendedAddress;Street;Locality;Region;PostalCode;Country
            const addressParts = decodedValue
              .split(';')
              .map(p => p.trim())
              .filter(p => p);
            contact.address = addressParts.join(', ');
            break;
          case 'ORG': // Organization
            contact.organization = decodedValue;
            break;
        }
      }

      // Only add contact if it has at least a name
      if (contact.name || contact.phones.length > 0 || contact.emails.length > 0) {
        if (!contact.name) {
          contact.name = contact.emails[0] || contact.phones[0] || 'Unknown';
        }
        contacts.push(contact);
      }
    }

    return contacts;
  };

  const decodeQuotedPrintable = (text: string): string => {
    try {
      return text.replace(/=([0-9A-F]{2})/gi, (match, hex) => String.fromCharCode(parseInt(hex, 16)));
    } catch {
      return text;
    }
  };

  const handleVCardFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setVcardFileName(file.name);
    const reader = new FileReader();

    reader.onload = e => {
      const content = e.target?.result as string;
      const parsed = parseVCard(content);
      setVcardContacts(parsed);
    };

    reader.readAsText(file);
  };

  const exportToCSV = () => {
    if (vcardContacts.length === 0) return;

    // CSV Header
    const headers = ['Name', 'Phones', 'Emails', 'Address', 'Organization'];

    // CSV Rows
    const rows = vcardContacts.map(contact => {
      const phones = contact.phones.join('; ');
      const emails = contact.emails.join('; ');

      // Escape fields that contain commas, quotes, or newlines
      const escapeCSV = (field: string) => {
        if (field.includes(',') || field.includes('"') || field.includes('\n')) {
          return `"${field.replace(/"/g, '""')}"`;
        }
        return field;
      };

      return [
        escapeCSV(contact.name),
        escapeCSV(phones),
        escapeCSV(emails),
        escapeCSV(contact.address),
        escapeCSV(contact.organization)
      ].join(',');
    });

    const csv = [headers.join(','), ...rows].join('\n');

    // Download CSV file
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', vcardFileName.replace('.vcf', '.csv') || 'contacts.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const fieldStyle: React.CSSProperties = {
    width: '100%',
    padding: '12px',
    fontSize: '14px',
    fontFamily: 'monospace',
    border: '1px solid #ddd',
    borderRadius: '4px',
    resize: 'vertical',
    minHeight: '200px'
  };

  const labelStyle: React.CSSProperties = {
    display: 'block',
    marginBottom: '8px',
    fontSize: '16px',
    fontWeight: '600',
    color: '#333'
  };

  const sectionStyle: React.CSSProperties = {
    marginBottom: '30px'
  };

  const helpTextStyle: React.CSSProperties = {
    fontSize: '13px',
    color: '#666',
    marginTop: '6px',
    fontStyle: 'italic'
  };

  const buttonStyle: React.CSSProperties = {
    padding: '10px 20px',
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '4px',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer'
  };

  // Filter vCard contacts by email and hidden status
  const filteredVcardContacts = vcardContacts
    .map((contact, originalIndex) => ({ contact, originalIndex }))
    .filter(({ contact, originalIndex }) => {
      // Email filter
      const passesEmailFilter =
        !showOnlyWithEmail ||
        (contact.emails.length > 0 && !contact.emails.every(email => email === '-' || email.trim() === ''));

      // Hidden filter
      const passesHiddenFilter = showHidden || !hiddenContacts.has(originalIndex);

      return passesEmailFilter && passesHiddenFilter;
    });

  // Handler for hiding a contact
  const handleHideContact = (originalIndex: number) => {
    setHiddenContacts(prev => new Set(prev).add(originalIndex));
  };

  // Extract email from a line of text
  const extractEmail = (line: string): string | null => {
    const emailRegex = /([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9_-]+)/;
    const match = line.match(emailRegex);
    return match ? match[1].toLowerCase() : null;
  };

  // Find duplicate emails in the text
  const handleFindDuplicates = () => {
    if (!duplicateRemoverText.trim()) {
      setDuplicateLineIndices(new Set());
      return;
    }

    const lines = duplicateRemoverText.split('\n');
    const emailMap = new Map<string, number[]>(); // email -> line indices

    // Build map of emails to line indices
    lines.forEach((line, index) => {
      const email = extractEmail(line);
      if (email) {
        if (!emailMap.has(email)) {
          emailMap.set(email, []);
        }
        emailMap.get(email)!.push(index);
      }
    });

    // Find duplicate indices (2nd, 3rd, etc. occurrences)
    const duplicates = new Set<number>();
    emailMap.forEach(indices => {
      if (indices.length > 1) {
        // Add all indices except the first one
        indices.slice(1).forEach(idx => duplicates.add(idx));
      }
    });

    setDuplicateLineIndices(duplicates);
  };

  // Delete highlighted duplicate rows
  const handleDeleteDuplicates = () => {
    if (duplicateLineIndices.size === 0) return;

    const lines = duplicateRemoverText.split('\n');
    const remainingLines = lines.filter((_, index) => !duplicateLineIndices.has(index));

    setDuplicateRemoverText(remainingLines.join('\n'));
    setDuplicateLineIndices(new Set()); // Clear highlights after deletion
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1200px', margin: '0 auto' }}>
      <header style={{ marginBottom: '40px' }}>
        <h1 style={{ fontSize: '2.5rem', color: '#333' }}>Contact Parser</h1>
        <p style={{ color: '#666', fontSize: '1.1rem', marginTop: '8px' }}>
          Parse and extract contact information from formatted text
        </p>
      </header>

      <div style={sectionStyle}>
        <label style={labelStyle}>Contact Information</label>
        <textarea
          style={fieldStyle}
          value={inputText}
          onChange={e => setInputText(e.target.value)}
          placeholder="Paste your contact information here&#10;Format: Store Name  email@example.com&#10;&#10;Example:&#10;Store A  store-a@example.com&#10;Store B  store-b@example.com"
        />
        <p style={helpTextStyle}>Use space-delimited format: fields separated by 2+ spaces or tabs</p>
      </div>

      <div style={sectionStyle}>
        <button
          style={buttonStyle}
          onClick={handleParse}
          onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#0056b3')}
          onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#007bff')}
        >
          Parse Contacts
        </button>
      </div>

      <div style={sectionStyle}>
        <label style={labelStyle}>Parsed Results ({parsedContacts.length})</label>
        <div
          style={{
            border: '1px solid #ddd',
            borderRadius: '4px',
            padding: '16px',
            backgroundColor: '#f9f9f9',
            minHeight: '200px'
          }}
        >
          {parsedContacts.length === 0 ? (
            <p style={{ color: '#999', fontStyle: 'italic' }}>
              Enter contact information above and click "Parse Contacts" to see results...
            </p>
          ) : (
            <div>
              <div style={{ marginBottom: '16px' }}>
                <button
                  style={{
                    ...buttonStyle,
                    fontSize: '14px',
                    padding: '8px 16px'
                  }}
                  onClick={() => {
                    const formatted = parsedContacts.map(c => `${c.storeName}\t${c.email}`).join('\n');
                    copyToClipboard(formatted);
                  }}
                  onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#0056b3')}
                  onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#007bff')}
                >
                  Copy All to Clipboard
                </button>
              </div>
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {parsedContacts.map((contact, index) => (
                  <li
                    key={index}
                    style={{
                      marginBottom: '12px',
                      padding: '12px',
                      backgroundColor: 'white',
                      borderRadius: '4px',
                      border: '1px solid #e0e0e0'
                    }}
                  >
                    <div style={{ marginBottom: '8px' }}>
                      <strong style={{ color: '#333', fontSize: '16px' }}>
                        {index + 1}. {contact.storeName}
                      </strong>
                    </div>
                    <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Email: {contact.email}</div>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      <button
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#28a745',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '13px',
                          cursor: 'pointer'
                        }}
                        onClick={() => copyToClipboard(contact.email)}
                        onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#218838')}
                        onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#28a745')}
                      >
                        Copy Email
                      </button>
                      <button
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#6c757d',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '13px',
                          cursor: 'pointer'
                        }}
                        onClick={() => copyToClipboard(`${contact.storeName}\t${contact.email}`)}
                        onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#5a6268')}
                        onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#6c757d')}
                      >
                        Copy Row
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <p style={helpTextStyle}>Click individual buttons to copy specific emails or rows</p>
      </div>

      <hr style={{ margin: '60px 0', border: 'none', borderTop: '2px solid #ddd' }} />

      <header style={{ marginBottom: '40px' }}>
        <h2 style={{ fontSize: '2rem', color: '#333' }}>vCard to CSV Converter</h2>
        <p style={{ color: '#666', fontSize: '1.1rem', marginTop: '8px' }}>
          Convert .vcf files containing multiple contacts to CSV format
        </p>
      </header>

      <div style={sectionStyle}>
        <label style={labelStyle}>Upload vCard File (.vcf)</label>
        <input
          type="file"
          accept=".vcf"
          onChange={handleVCardFileUpload}
          style={{
            padding: '12px',
            fontSize: '14px',
            border: '1px solid #ddd',
            borderRadius: '4px',
            backgroundColor: 'white',
            cursor: 'pointer',
            width: '100%'
          }}
        />
        <p style={helpTextStyle}>Supports vCard 2.1 and 3.0 formats with multiple contacts</p>
      </div>

      {vcardContacts.length > 0 && (
        <>
          <div style={sectionStyle}>
            <button
              style={buttonStyle}
              onClick={exportToCSV}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#0056b3')}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#007bff')}
            >
              Download CSV
            </button>
          </div>

          <div style={{ ...sectionStyle, marginTop: '-15px', display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button
              style={{
                ...buttonStyle,
                backgroundColor: showOnlyWithEmail ? '#28a745' : '#6c757d',
                fontSize: '14px',
                padding: '8px 16px'
              }}
              onClick={() => setShowOnlyWithEmail(!showOnlyWithEmail)}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = showOnlyWithEmail ? '#218838' : '#5a6268')}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = showOnlyWithEmail ? '#28a745' : '#6c757d')}
            >
              {showOnlyWithEmail ? 'Show All' : 'Show With Email Only'}
            </button>
            <button
              style={{
                ...buttonStyle,
                backgroundColor: isCompactView ? '#17a2b8' : '#6c757d',
                fontSize: '14px',
                padding: '8px 16px'
              }}
              onClick={() => setIsCompactView(!isCompactView)}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = isCompactView ? '#138496' : '#5a6268')}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = isCompactView ? '#17a2b8' : '#6c757d')}
            >
              {isCompactView ? 'Full View' : 'Compact View'}
            </button>
            {hiddenContacts.size > 0 && (
              <button
                style={{
                  ...buttonStyle,
                  backgroundColor: showHidden ? '#ffc107' : '#6c757d',
                  fontSize: '14px',
                  padding: '8px 16px'
                }}
                onClick={() => setShowHidden(!showHidden)}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = showHidden ? '#e0a800' : '#5a6268')}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = showHidden ? '#ffc107' : '#6c757d')}
              >
                {showHidden ? `Hide Hidden (${hiddenContacts.size})` : `Show Hidden (${hiddenContacts.size})`}
              </button>
            )}
          </div>

          <div style={sectionStyle}>
            <label style={labelStyle}>
              Parsed Contacts (
              {(showOnlyWithEmail || hiddenContacts.size > 0) && !showHidden
                ? `${filteredVcardContacts.length} of ${vcardContacts.length}`
                : vcardContacts.length}
              )
            </label>
            <div
              style={{
                border: '1px solid #ddd',
                borderRadius: '4px',
                padding: '16px',
                backgroundColor: '#f9f9f9',
                maxHeight: '600px',
                overflowY: 'auto'
              }}
            >
              <div style={{ overflowX: 'auto' }}>
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    backgroundColor: 'white'
                  }}
                >
                  <thead>
                    <tr style={{ backgroundColor: '#f0f0f0' }}>
                      <th
                        style={{
                          padding: '12px',
                          textAlign: 'left',
                          borderBottom: '2px solid #ddd',
                          fontWeight: '600'
                        }}
                      >
                        #
                      </th>
                      <th
                        style={{
                          padding: '12px',
                          textAlign: 'left',
                          borderBottom: '2px solid #ddd',
                          fontWeight: '600'
                        }}
                      >
                        Name
                      </th>
                      {!isCompactView && (
                        <th
                          style={{
                            padding: '12px',
                            textAlign: 'left',
                            borderBottom: '2px solid #ddd',
                            fontWeight: '600'
                          }}
                        >
                          Phones
                        </th>
                      )}
                      <th
                        style={{
                          padding: '12px',
                          textAlign: 'left',
                          borderBottom: '2px solid #ddd',
                          fontWeight: '600'
                        }}
                      >
                        Emails
                      </th>
                      {!isCompactView && (
                        <>
                          <th
                            style={{
                              padding: '12px',
                              textAlign: 'left',
                              borderBottom: '2px solid #ddd',
                              fontWeight: '600'
                            }}
                          >
                            Address
                          </th>
                          <th
                            style={{
                              padding: '12px',
                              textAlign: 'left',
                              borderBottom: '2px solid #ddd',
                              fontWeight: '600'
                            }}
                          >
                            Organization
                          </th>
                        </>
                      )}
                      <th
                        style={{
                          padding: '12px',
                          textAlign: 'left',
                          borderBottom: '2px solid #ddd',
                          fontWeight: '600'
                        }}
                      >
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredVcardContacts.map(({ contact, originalIndex }, displayIndex) => (
                      <tr key={originalIndex} style={{ borderBottom: '1px solid #eee' }}>
                        <td style={{ padding: '12px', fontSize: '14px', color: '#666' }}>{displayIndex + 1}</td>
                        <td style={{ padding: '12px', fontSize: '14px', fontWeight: '500' }}>{contact.name}</td>
                        {!isCompactView && (
                          <td style={{ padding: '12px', fontSize: '14px' }}>
                            {contact.phones.length > 0 ? contact.phones.join('; ') : '-'}
                          </td>
                        )}
                        <td style={{ padding: '12px', fontSize: '14px' }}>
                          {contact.emails.length > 0 ? contact.emails.join('; ') : '-'}
                        </td>
                        {!isCompactView && (
                          <>
                            <td style={{ padding: '12px', fontSize: '14px' }}>{contact.address || '-'}</td>
                            <td style={{ padding: '12px', fontSize: '14px' }}>{contact.organization || '-'}</td>
                          </>
                        )}
                        <td style={{ padding: '12px', fontSize: '14px' }}>
                          <button
                            style={{
                              padding: '6px 12px',
                              backgroundColor: '#dc3545',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '13px',
                              cursor: 'pointer'
                            }}
                            onClick={() => handleHideContact(originalIndex)}
                            onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#c82333')}
                            onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#dc3545')}
                          >
                            Hide
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <p style={helpTextStyle}>Multiple phones and emails are separated by semicolons</p>
          </div>
        </>
      )}

      <hr style={{ margin: '60px 0', border: 'none', borderTop: '2px solid #ddd' }} />

      <header style={{ marginBottom: '40px' }}>
        <h2 style={{ fontSize: '2rem', color: '#333' }}>Duplicate Remover</h2>
        <p style={{ color: '#666', fontSize: '1.1rem', marginTop: '8px' }}>Remove duplicate contacts from your list</p>
      </header>

      <div style={sectionStyle}>
        <label style={labelStyle}>Paste Contact List</label>
        <textarea
          style={fieldStyle}
          value={duplicateRemoverText}
          onChange={e => {
            setDuplicateRemoverText(e.target.value);
            setDuplicateLineIndices(new Set()); // Clear highlights when text changes
          }}
          placeholder="Paste your contact list here..."
        />
        <p style={helpTextStyle}>Paste your contact list to remove duplicates</p>
      </div>

      {duplicateRemoverText && (
        <div style={sectionStyle}>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'center' }}>
            <button
              style={buttonStyle}
              onClick={handleFindDuplicates}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#0056b3')}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#007bff')}
            >
              Find Duplicates
            </button>
            {duplicateLineIndices.size > 0 && (
              <button
                style={{
                  ...buttonStyle,
                  backgroundColor: '#dc3545'
                }}
                onClick={handleDeleteDuplicates}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#c82333')}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#dc3545')}
              >
                Delete Highlighted
              </button>
            )}
          </div>
          {duplicateLineIndices.size > 0 && (
            <p style={{ ...helpTextStyle, marginTop: '12px', color: '#007bff' }}>
              Found {duplicateLineIndices.size} duplicate line(s) highlighted in blue below
            </p>
          )}
        </div>
      )}

      {duplicateRemoverText && (
        <div style={sectionStyle}>
          <label style={labelStyle}>Pasted Content</label>
          <div
            style={{
              border: '1px solid #ddd',
              borderRadius: '4px',
              padding: '16px',
              backgroundColor: '#f9f9f9',
              minHeight: '200px'
            }}
          >
            {duplicateRemoverText.split('\n').map((line, index) => {
              const isDuplicate = duplicateLineIndices.has(index);
              return (
                <div
                  key={index}
                  style={{
                    fontFamily: 'monospace',
                    fontSize: '14px',
                    padding: '4px 8px',
                    marginBottom: '2px',
                    border: isDuplicate ? '2px solid #007bff' : '2px solid transparent',
                    borderRadius: '4px',
                    backgroundColor: isDuplicate ? '#e7f3ff' : 'transparent',
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word'
                  }}
                >
                  {line || '\u00A0'}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
